import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogIn, LogOut, Loader2, Radio, Check, X as XIcon, Camera } from "lucide-react";
import PJPCoin from "@/components/PJPCoin";

/**
 * PJP Digital ID — physical-card style.
 * Features:
 *  • Big member photo, name, DOB, PJP-ID number, cert, membership expiry
 *  • Live scan-heartbeat: while ANY device is viewing this badge, the green
 *    chasing border activates + the PJP coin rotates + top banner reads
 *    "PJP VERIFIED — IDENTITY CONFIRMED" with pulsing dot.
 */
export default function DigitalId() {
  const { token } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [activeScanners, setActiveScanners] = useState(0);
  const [expiresAt, setExpiresAt] = useState(null);   // ISO — soonest external heartbeat expiry
  const photoInputRef = useRef(null);
  const deviceId = useRef(localStorage.getItem("pjp_device_id") || `dev-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => { localStorage.setItem("pjp_device_id", deviceId.current); }, []);

  const load = () => api.get(`/id/${token}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Automatic heartbeat every 20s — a single heartbeat opens a 60s live window.
  // Owner heartbeats are flagged server-side and don't self-trigger animation.
  useEffect(() => {
    if (!token) return;
    const beat = () => api
      .post(`/id/${token}/scan-heartbeat`, {}, { headers: { "X-PJP-Device": deviceId.current } })
      .then((r) => {
        setActiveScanners(r.data.active_scanners || 0);
        const soonest = (r.data.viewers || []).reduce(
          (min, v) => (!min || v.expires_at < min) ? v.expires_at : min, null,
        );
        setExpiresAt(soonest);
      })
      .catch(() => {});
    beat();
    const id = setInterval(beat, 20000);   // 20s heartbeat keeps a 60s window alive
    return () => clearInterval(id);
  }, [token]);

  // Live-status poll every 4s so owner sees viewers appear/disappear promptly
  useEffect(() => {
    if (!token) return;
    const tick = () => api.get(`/id/${token}/live-status`)
      .then((r) => {
        setActiveScanners(r.data.active_scanners || 0);
        const soonest = (r.data.viewers || []).reduce(
          (min, v) => (!min || v.expires_at < min) ? v.expires_at : min, null,
        );
        setExpiresAt(soonest);
      })
      .catch(() => {});
    const id = setInterval(tick, 4000);
    return () => clearInterval(id);
  }, [token]);

  // Countdown to display remaining seconds
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!expiresAt) { setRemaining(0); return; }
    const tick = () => {
      const s = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemaining(s);
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [expiresAt]);

  const scan = async (direction) => {
    if (!user) { toast.error("Sign in to scan this badge"); return; }
    setScanning(true);
    try {
      const activeTail = data.active_tails?.[0]?.tail_number;
      await api.post(`/id/${token}/scan`, { direction, tail_number: activeTail });
      toast.success(`${data.name} scanned ${direction === "in" ? "IN" : "OUT"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const uploadIdPhoto = async (file) => {
    if (!file) return;
    try {
      toast.loading("Uploading ID photo…", { id: "id-photo" });
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "id_photo");
      const up = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/id/profile", { id_photo_file_id: up.data.id });
      toast.success("ID photo updated", { id: "id-photo" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed", { id: "id-photo" }); }
  };

  if (!data) return <div className="min-h-[80vh] flex items-center justify-center text-white/60"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>;
  if (data.error) return <div className="min-h-[80vh] flex items-center justify-center text-white/60">Digital ID not found.</div>;

  const isOwner = user && user.id === data.member_id;
  const scanLive = activeScanners > 0;
  const url = `${window.location.origin}/id/${token}`;
  const isVerified = data.faa_verified && data.membership_status === "active";
  const nameParts = (data.name || "Member").split(" ");
  const first = nameParts[0] || "";
  const rest = nameParts.slice(1).join(" ");
  const memberNumber = data.pjp_member_number ? String(data.pjp_member_number).padStart(5, "0") : "—";
  const dob = data.dob ? new Date(data.dob).toLocaleDateString("en-US") : null;
  const expDate = data.membership_expires_at ? new Date(data.membership_expires_at) : null;
  const expYear = expDate?.getFullYear();
  const expMon = expDate?.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const expDay = expDate?.getDate();
  const membershipTier = (data.membership_tier_slug || "First Class").replace(/_/g, " ").toUpperCase();
  const roleLabel = (data.role || "MEMBER").toUpperCase();
  const cert = data.certificates?.[0] || {};
  const certLabel = cert.number
    ? `${(cert.type || "ATP").toUpperCase()} ${cert.number}`
    : (data.pilot_certificate_number ? `ATP ${data.pilot_certificate_number}` : "—");

  return (
    <div className="min-h-screen bg-obsidian py-6 px-4 flex items-center justify-center" data-testid="digital-id-page">
      {/* Chasing-border animation CSS */}
      <style>{`
        @keyframes pjp-chase {
          0%   { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
        @keyframes pjp-coin {
          0%   { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }
        @keyframes pjp-crew-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .pjp-id-shell {
          position: relative;
          border-radius: 20px;
          padding: 3px;
          background: linear-gradient(90deg, rgba(46,213,115,0) 0%, rgba(46,213,115,0.75) 25%, rgba(46,213,115,1) 50%, rgba(46,213,115,0.75) 75%, rgba(46,213,115,0) 100%);
          background-size: 300% 100%;
          background-position: 0% 50%;
        }
        .pjp-id-shell.scan-live {
          animation: pjp-chase 1.6s linear infinite;
          box-shadow: 0 0 40px rgba(46,213,115,0.45);
        }
        .pjp-coin { display:inline-block; transform-style: preserve-3d; }
        .pjp-coin.scan-live { animation: pjp-coin 2.8s linear infinite; }
        .pjp-crew-marquee { display:flex; white-space: nowrap; }
        .pjp-crew-marquee > span { padding-right: 24px; }
        .pjp-crew-marquee.scan-live { animation: pjp-crew-scroll 22s linear infinite; }
      `}</style>

      <div className="w-full max-w-md">
        {/* Card outer shell (chasing border) */}
        <div className={`pjp-id-shell ${scanLive ? "scan-live" : ""}`}>
          <div className="rounded-[17px] overflow-hidden" style={{ background: "#0A0F1A" }}>
            {/* Top verified banner */}
            <div
              className="py-2.5 px-4 flex items-center justify-center gap-2 mono text-[11px] font-bold tracking-widest"
              style={{ background: scanLive ? "#2ED573" : "#0A1F14", color: scanLive ? "#0A1F14" : "#4EDF8B", borderBottom: "1px solid rgba(46,213,115,0.35)" }}
            >
              <span className={`h-2 w-2 rounded-full ${scanLive ? "animate-pulse" : ""}`} style={{ background: scanLive ? "#0A1F14" : "#4EDF8B" }} />
              PJP VERIFIED — IDENTITY {isVerified ? "CONFIRMED" : "PENDING"}
              <Check className="h-3.5 w-3.5" />
            </div>

            {/* Header */}
            <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-white/[0.08]">
              <div style={{ width: 44, height: 44, flexShrink: 0 }}>
                <PJPCoin speed={0.2} spinning={scanLive} testId="header-coin" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-white text-[15px] tracking-tight leading-tight">PRIVATE JET PILOTS</div>
                <div className="mono text-[10px] tracking-[0.24em] text-white/50 uppercase">Aviation Identity Card</div>
              </div>
            </div>

            {/* Photo + Meta grid */}
            <div className="grid grid-cols-[132px_1fr] gap-3 p-4">
              {/* Photo */}
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10">
                {data.id_photo_url || data.avatar_url ? (
                  <img
                    src={(data.id_photo_url || data.avatar_url).startsWith("/")
                      ? `${process.env.REACT_APP_BACKEND_URL || ""}${data.id_photo_url || data.avatar_url}`
                      : (data.id_photo_url || data.avatar_url)}
                    alt=""
                    className="w-full h-full object-cover"
                    data-testid="id-photo"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-white/25 text-center p-2">
                    <div>
                      <Camera className="h-8 w-8 mx-auto mb-1" />
                      <div className="mono text-[9px] tracking-widest">NO PHOTO</div>
                    </div>
                  </div>
                )}
                {isOwner && (
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    data-testid="btn-capture-id-photo"
                    className="absolute inset-x-2 bottom-2 rounded-full bg-black/70 backdrop-blur border border-white/20 mono text-[10px] tracking-widest text-white py-1.5 flex items-center justify-center gap-1"
                  >
                    <Camera className="h-3 w-3" /> {(data.id_photo_url || data.avatar_url) ? "RETAKE" : "CAPTURE"}
                  </button>
                )}
                {isOwner && (
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={(e) => uploadIdPhoto(e.target.files?.[0])}
                    data-testid="input-id-photo"
                  />
                )}
              </div>
              {/* Right column: ID + cert + tier */}
              <div className="space-y-3">
                <MetaRow label="MEMBER ID" value={`PJP-ID ${memberNumber}`} />
                <MetaRow label="CERTIFICATE" value={certLabel} />
                <MetaRow label={membershipTier} value={expDate ? `Exp ${expDate.toLocaleString("en-US", { month: "short" }).toUpperCase()} ${expDate.getFullYear()}` : "Not active"} tone="orange" />
              </div>
            </div>

            {/* Name block */}
            <div className="px-4 pb-3">
              <div className="font-display font-bold text-white leading-[0.95] tracking-tight" style={{ fontSize: "44px" }}>
                {rest ? `${rest},` : first}
              </div>
              {rest && <div className="font-display font-bold text-white leading-[0.95] tracking-tight" style={{ fontSize: "44px" }}>{first}</div>}
              {dob && <div className="mono text-[11px] tracking-widest text-white/60 uppercase mt-2">DOB {dob}</div>}
              <div className="mt-3 inline-block px-3 py-1.5 rounded-md bg-white text-black mono text-[11px] font-bold tracking-widest">
                {roleLabel}
              </div>
            </div>

            {/* CREW marquee */}
            <div className="relative overflow-hidden py-2 border-t border-b border-white/[0.06]" style={{ background: "#0A1220" }}>
              <div className={`pjp-crew-marquee ${scanLive ? "scan-live" : ""}`}>
                <span className="font-display font-black tracking-tight text-[42px]" style={{ color: "#F5C842", opacity: 0.35 }}>
                  {" CREW · ".repeat(8)}
                </span>
                <span className="font-display font-black tracking-tight text-[42px]" style={{ color: "#F5C842", opacity: 0.35 }}>
                  {" CREW · ".repeat(8)}
                </span>
              </div>
            </div>

            {/* Expiration bar */}
            <div className="px-4 py-3 flex items-center justify-between gap-2 border-b border-white/[0.06]">
              <div style={{ width: 30, height: 30, flexShrink: 0 }}>
                <PJPCoin speed={0.2} spinning={scanLive} testId="exp-coin-left" />
              </div>
              <div className="mono text-[10px] tracking-widest text-white/45 uppercase">EXP</div>
              <div className="font-display font-bold text-white text-2xl tracking-tight flex items-baseline gap-1.5">
                {expYear ? (<><span>{expYear}</span><span className="mono text-[10px] text-white/50 tracking-widest">{expMon}</span><span>{expDay}</span></>) : "PENDING"}
              </div>
              <div className="mono text-[10px] tracking-widest text-white/45 uppercase">EXP</div>
              <div style={{ width: 30, height: 30, flexShrink: 0 }}>
                <PJPCoin speed={0.2} spinning={scanLive} testId="exp-coin-right" />
              </div>
            </div>

            {/* QR */}
            <div className="px-4 py-4 flex items-center gap-3">
              <div className="bg-white p-1 rounded-md">
                <QRCodeSVG value={url} size={70} bgColor="#ffffff" fgColor="#050810" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-0.5">SCAN TO VERIFY</div>
                <div className="text-[11px] text-white/70 leading-snug">Airport security: scan QR to view verified credentials on <span className="text-sky">privatejetpilots.com</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Live scan status pill — replaces manual START/STOP toggle now that
            any QR view triggers a 60s live window automatically. */}
        {scanLive ? (
          <div
            data-testid="scan-live-chip"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold mono text-xs tracking-widest min-h-[48px]"
            style={{ background: "rgba(46,213,115,0.14)", border: "1px solid rgba(46,213,115,0.55)", color: "#4EDF8B", boxShadow: "0 0 18px rgba(46,213,115,0.25)" }}
          >
            <span className="h-2 w-2 rounded-full bg-avail animate-pulse" />
            LIVE SCAN · {activeScanners} DEVICE{activeScanners > 1 ? "S" : ""} VIEWING · {remaining}S
          </div>
        ) : isOwner && (
          <div
            data-testid="scan-idle-chip"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl mono text-[11px] tracking-widest min-h-[48px]"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)" }}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: "rgba(255,255,255,0.35)" }} />
            IDLE · SCAN QR CODE TO ACTIVATE LIVE BADGE
          </div>
        )}

        {/* FBO scan actions */}
        {user && !isOwner && (
          <div className="mt-3 grid grid-cols-2 gap-2" data-testid="fbo-scan-actions">
            <button onClick={() => scan("in")} disabled={scanning} data-testid="btn-scan-in"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-semibold text-sm min-h-[48px]"
              style={{ background: "rgba(46,213,115,0.15)", border: "1px solid rgba(46,213,115,0.55)", color: "#4EDF8B" }}>
              <LogIn className="h-4 w-4" /> SCAN IN
            </button>
            <button onClick={() => scan("out")} disabled={scanning} data-testid="btn-scan-out"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-semibold text-sm min-h-[48px]"
              style={{ background: "rgba(235,87,87,0.10)", border: "1px solid rgba(235,87,87,0.45)", color: "#FF7A7A" }}>
              <LogOut className="h-4 w-4" /> SCAN OUT
            </button>
          </div>
        )}

        {/* Ramp status */}
        {data.current_status && data.current_status !== "unknown" && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border px-3 py-2"
               style={{ background: data.current_status === "on_property" ? "rgba(46,213,115,0.10)" : "rgba(255,255,255,0.04)", borderColor: data.current_status === "on_property" ? "rgba(46,213,115,0.45)" : "rgba(255,255,255,0.12)" }}
               data-testid="id-ramp-status">
            <Radio className={`h-3.5 w-3.5 ${data.current_status === "on_property" ? "text-avail animate-pulse" : "text-white/40"}`} />
            <div className="flex-1 min-w-0">
              <div className="mono text-[10px] tracking-widest uppercase" style={{ color: data.current_status === "on_property" ? "#4EDF8B" : "rgba(255,255,255,0.5)" }}>
                {data.current_status === "on_property" ? "ON PROPERTY" : "DEPARTED"}
              </div>
              <div className="text-[11px] text-white/60 truncate">{data.last_scan_fbo_name}{data.last_scan_icao ? ` · ${data.last_scan_icao}` : ""}</div>
            </div>
          </div>
        )}

        <div className="mt-3 text-center text-[11px] text-white/40 mono">Shareable · NFC-ready · Verified by PJP</div>
      </div>
    </div>
  );
}

function MetaRow({ label, value, tone }) {
  return (
    <div>
      <div className="mono text-[9px] tracking-[0.22em] uppercase text-white/45">{label}</div>
      <div className={`font-display font-semibold text-base tracking-tight ${tone === "orange" ? "text-orange-hud" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
